import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createUser,
  deleteUser,
  getDepartments,
  getMe,
  getRoles,
  getUser,
  getUserHistory,
  getUsers,
  updateUser,
  type CreateUserRequest,
  type FormEvent,
  type MeResponse,
  type RoleInfo,
  type UpdateUserRequest,
  type User,
} from '../api/users';

/**
 * Hook to fetch the signed-in user's context and the server's environment. `enabled: false` reads the cached answer
 * without ever fetching it (the page title does that).
 */
export function useMe(options?: { enabled?: boolean }) {
  return useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: getMe,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

/** All users (optionally including soft-deleted rows). One row per email. */
export function useUsers(includeDeleted = false) {
  return useQuery<User[]>({
    queryKey: ['users', { includeDeleted }],
    queryFn: () => getUsers(includeDeleted),
    retry: false,
  });
}

export function useUser(email: string, enabled = true) {
  return useQuery<User>({
    queryKey: ['users', 'one', email],
    queryFn: () => getUser(email),
    enabled,
    retry: false,
  });
}

export function useRoles(enabled = true) {
  return useQuery<RoleInfo[]>({
    queryKey: ['users', 'roles'],
    queryFn: getRoles,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useDepartments(enabled = true) {
  return useQuery<string[]>({
    queryKey: ['users', 'departments'],
    queryFn: getDepartments,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useUserHistory(email: string, enabled = true) {
  return useQuery<FormEvent[]>({
    queryKey: ['users', 'history', email],
    queryFn: () => getUserHistory(email),
    enabled,
    retry: false,
  });
}

/** Helper: any mutation on a user row should invalidate the cached list + per-user queries. */
function useInvalidateUsers() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['users'] });
}

export function useCreateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation<User, Error, CreateUserRequest>({
    mutationFn: createUser,
    onSuccess: invalidate,
  });
}

export function useUpdateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation<User, Error, { email: string; body: UpdateUserRequest }>({
    mutationFn: ({ email, body }) => updateUser(email, body),
    onSuccess: invalidate,
  });
}

export function useDeleteUser() {
  const invalidate = useInvalidateUsers();
  return useMutation<User, Error, string>({
    mutationFn: (email: string) => deleteUser(email),
    onSuccess: invalidate,
  });
}
